import { useState } from "react";
import {
  Box,
  Button,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CertificateInfoModal from "./CertificateInfoModal";
import BorrowerCertUploadModal from "./BorrowerCertUploadModal";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import {
  BorrowerDetail,
  EmissionSource,
  EmissionYear,
} from "../../../models/dto/cat15/cat15.types";
import {
  FeCell,
  FeHeadCell,
  FeRow,
  FeTable,
  FeTableScroll,
  FeTableWrap,
} from "../../../components/cat15/FeTable";
import FePagination from "../../../components/cat15/FePagination";
import { deleteBorrowerCertificate } from "../../../service/cat15.service";
import alertService from "../../../components/alert/alertService";
import { formatDate, formatNumber } from "../format";

interface Props {
  borrower: BorrowerDetail;
  /** Re-fetch the borrower detail after a certificate is attached. */
  onChanged?: () => void;
}

// Source pill colors — Figma node 6100:52747 (green audited / amber self-reported).
const sourceStyle: Record<
  EmissionSource,
  { color: string; bg: string }
> = {
  audited: {
    color: "var(--status-approved-complete-active-text)",
    // Figma "Audit certified" pill uses the lighter --green3 tint (not the status-active bg).
    bg: "var(--green3)",
  },
  "self-reported": {
    color: "var(--status-pending-text)",
    bg: "var(--status-pending-bg)",
  },
  fallback: {
    color: "var(--status-draft-inactive-text)",
    bg: "var(--status-draft-inactive-bg)",
  },
};

/** Emissions tab (read-only GHG data + per-year certificate attach) — Figma node 6100:52747. */
const DetailEmissions = ({ borrower, onChanged }: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Year whose Upload Audit Certificate modal is open (from "+ Certificate" or Replace).
  const [uploadYear, setUploadYear] = useState<number | null>(null);
  // Row whose attached certificate is open in the Certificate Information modal.
  const [previewRow, setPreviewRow] = useState<EmissionYear | null>(null);
  const rows = borrower.emissions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleDeleteCert = async () => {
    if (!previewRow) return;
    const year = previewRow.year;
    const ok = await alertService.confirm(
      t("Cert-Delete-Confirm-Title"),
      t("Cert-Delete-Confirm-Text", { year }),
      "Delete",
      "var(--red)",
      true,
      undefined,
      "Cancel",
    );
    if (!ok) return;
    setPreviewRow(null);
    try {
      await deleteBorrowerCertificate(borrower.id, year);
      alertService.success(t("Cert-Delete-Success", { year }));
      onChanged?.();
    } catch {
      // Error alert is surfaced by handleApiError inside the service.
    }
  };


  return (
    <Box display="flex" flexDirection="column" gap="16px">
      {/* Read-only source banner */}
      <Box
        display="flex"
        alignItems="center"
        gap="4px"
        bgcolor="var(--green3)"
        borderRadius="8px"
        p="8px 12px"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={24}
          height={24}
          flexShrink={0}
          color="var(--green)"
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Typography fontSize="12px" lineHeight="16px" color="var(--gray1)">
          {t("Emissions-Bank-Note")}
        </Typography>
      </Box>

      <FeTableWrap>
        <FeTableScroll>
        <FeTable>
          <TableHead>
            <TableRow>
              <FeHeadCell sx={{ width: 80 }}>{t("Col-Year")}</FeHeadCell>
              <FeHeadCell align="right">{t("Col-Scope1")}</FeHeadCell>
              <FeHeadCell align="right">{t("Col-Scope2")}</FeHeadCell>
              <FeHeadCell align="right">{t("Col-Total-S1-S2")}</FeHeadCell>
              <FeHeadCell align="center">{t("Col-Last-Updated")}</FeHeadCell>
              <FeHeadCell align="center">{t("Col-Source")}</FeHeadCell>
              <FeHeadCell align="center">{t("Col-Certificate")}</FeHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((e, idx) => {
              const st = sourceStyle[e.source];
              // Match FeRow's zebra stripe so the cert button blends into its row
              // (odd → white, even → --gray6). See FeTable FeRow.
              const rowBg = idx % 2 === 0 ? "var(--white)" : "var(--gray6)";
              return (
                <FeRow key={e.year}>
                  <FeCell>{e.year}</FeCell>
                  <FeCell align="right">{formatNumber(e.scope1)}</FeCell>
                  <FeCell align="right">{formatNumber(e.scope2)}</FeCell>
                  <FeCell align="right" sx={{ fontWeight: 600 }}>
                    {formatNumber(e.s1s2)}
                  </FeCell>
                  {/* Figma: date is navy (--dark), not grey. */}
                  <FeCell align="center">
                    {e.lastUpdated ? formatDate(e.lastUpdated) : "—"}
                  </FeCell>
                  <FeCell align="center">
                    {/* Source pill — Figma nodes 6611:19944 / 6611:19988: SemiBold 10/16, px 8, py 2, radius 12. */}
                    <Box
                      component="span"
                      display="inline-flex"
                      alignItems="center"
                      px="8px"
                      py="2px"
                      borderRadius="12px"
                      color={st.color}
                      bgcolor={st.bg}
                      sx={{ fontSize: "10px", fontWeight: 600, lineHeight: "16px" }}
                    >
                      {t(`Source-${e.source}`)} · {t("DQS")} {e.dqs}
                    </Box>
                  </FeCell>
                  <FeCell align="center">
                    {e.certificateAttached ? (
                      // Attached → "View" opens the Certificate Information modal (Figma node 6611:19944).
                      <Button
                        variant="text"
                        onClick={() => setPreviewRow(e)}
                        sx={{
                          minWidth: 0,
                          gap: "4px",
                          px: "8px",
                          py: "4px",
                          borderRadius: "4px",
                          bgcolor: rowBg,
                          color: "var(--dark)",
                          textTransform: "none",
                          fontSize: "14px",
                          fontWeight: 500,
                          lineHeight: "24px",
                          letterSpacing: 0,
                          "&:hover": { bgcolor: "var(--green3)" },
                        }}
                      >
                        <Box
                          component="img"
                          src="/img/eye.svg"
                          alt=""
                          width={24}
                          height={24}
                        />
                        {t("Cert-View-Action")}
                      </Button>
                    ) : (
                      // Not attached → "＋ Certificate" opens the Upload Audit Certificate modal (Figma node 6611:19988).
                      <Button
                        variant="text"
                        onClick={() => setUploadYear(e.year)}
                        sx={{
                          minWidth: 0,
                          gap: "4px",
                          px: "8px",
                          py: "4px",
                          borderRadius: "4px",
                          bgcolor: rowBg,
                          color: "var(--dark)",
                          textTransform: "none",
                          fontSize: "14px",
                          fontWeight: 500,
                          lineHeight: "24px",
                          letterSpacing: 0,
                          "&:hover": { bgcolor: "var(--green3)" },
                        }}
                      >
                        <Box
                          component="img"
                          src="/img/plus-navy.svg"
                          alt=""
                          width={24}
                          height={24}
                        />
                        {t("Col-Certificate")}
                      </Button>
                    )}
                  </FeCell>
                </FeRow>
              );
            })}
          </TableBody>
        </FeTable>
        </FeTableScroll>
        <FePagination
          count={borrower.emissions.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
        />
      </FeTableWrap>

      {/* Certificate Information — opens from a row's "View" action for the attached cert. */}
      <CertificateInfoModal
        open={previewRow !== null}
        onClose={() => setPreviewRow(null)}
        borrowerName={borrower.name}
        row={previewRow}
        onReplace={() => {
          if (!previewRow) return;
          const year = previewRow.year;
          setPreviewRow(null);
          setUploadYear(year);
        }}
        onDelete={handleDeleteCert}
      />

      {/* Upload Audit Certificate — opens from "+ Certificate" or the preview's Replace. */}
      {uploadYear !== null && (
        <BorrowerCertUploadModal
          open={uploadYear !== null}
          onClose={() => setUploadYear(null)}
          borrowerId={borrower.id}
          year={uploadYear}
          onUploaded={() => {
            setUploadYear(null);
            onChanged?.();
          }}
        />
      )}
    </Box>
  );
};

export default DetailEmissions;
